/**
 * Shared client-side attachment limits (feature 051; ceiling unified by 055).
 *
 * ONE source for the advisory web-side attachment rules, imported by BOTH the
 * member-conversation reply composer (`member-cases/ReplyComposer.tsx`, 040/055) and the
 * member-message compose upload (`member-messaging/compose`, 051) — so the type
 * allow-list and the size ceiling cannot drift between the two surfaces
 * (Constitution P VIII: one authoritative representation).
 *
 * These are ADVISORY, fast-feedback limits. The authoritative enforcers are
 * downstream: the server magic-byte sniff
 * (`apps/api/src/modules/member-cases/attachment-content-sniff.ts`) for TYPE
 * (415); for SIZE (413) the BFF upload route + sniff (25 MB, `MAX_ATTACHMENT_BYTES`)
 * and then the Integration Service (observed ~10 MB runtime limit). The advisory
 * ceiling here MATCHES the BFF cap, so Oma+ never promises a size its own server
 * refuses; only the IS sits lower, and that residual gap is O-020. Oma+ talks
 * only through the IS; the IS `/v1` attachment endpoint validates neither type
 * nor count, so Oma+ owns the type allow-list here and mirrors it to the sniff
 * set.
 *
 * ── 055: one ceiling, and the optional-limit convention ─────────────────────
 *
 * The customer's decision (2026-09-29) was that attachment limits are a
 * hypothetical problem today: remove them, or parameterise them and set them
 * very high. Two consequences:
 *
 *   1. The reply path (formerly 25 MB) and the compose path (formerly 10 MB)
 *      now share ONE `MEMBER_ATTACHMENT_MAX_BYTES`. That split was already
 *      flagged in this file as the 051 FR-003a / O-020 follow-up; 055 FR-017
 *      closes it.
 *   2. Every limit is OPTIONAL — typed `number | undefined`, where `undefined`
 *      means "no limit, and show nothing about it" (FR-016a). This is the
 *      prototype's own convention (`REPLY_MAX_CHARS` / `REPLY_MAX_FILES` /
 *      `REPLY_MAX_FILE_BYTES` in its `MessageDetail.tsx`), adopted deliberately:
 *      it lets a limit be switched off without leaving orphaned counter or
 *      hint copy behind on the surface.
 *
 * The TYPE allow-list deliberately SURVIVES. It is not a "limit" in the
 * customer's sense — it is an advisory mirror of the server's magic-byte sniff,
 * and dropping it would turn fast, specific client feedback into slower,
 * worse-explained upstream 415s (FR-019).
 */

/**
 * Advisory MIME allow-list = the server magic-byte sniff set (shared, must not drift).
 *
 * NOT "exactly" the sniff set: the sniff validates by magic bytes, and its
 * `ooxml` arm accepts ANY ZIP container (also PPTX/ODT/JAR/.zip), so the server
 * accepts a broader byte-class than these five MIME strings. The pre-check
 * reduces avoidable `415`s but the sniff stays authoritative — an empty or
 * spoofed-MIME file, or one whose bytes match no signature, can still `415`
 * (051 SC-007).
 *
 * LOCKSTEP: the member-facing copy naming these types is hand-authored prose,
 * NOT derived from this set — `compose.attachment.acceptedNote` /
 * `.blockUnsupported` in `i18n/messages/{fi,en,sv}/memberMessagingCompose.json`
 * and `myCases.detail.uploadLabel` / `.uploadUnsupported` in
 * `i18n/messages/{fi,en,sv}/myCases.json`. Change this set and those strings go
 * stale silently in three languages.
 */
export const ALLOWED_ATTACHMENT_MIME: ReadonlySet<string> = new Set<string>([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
]);

/**
 * The single advisory size ceiling for every member attachment surface
 * (055 FR-016 / FR-017). Bound to 25 MB by `DL-027` in
 * `.specify/memory/DECISIONS.md`.
 *
 * Value and reasoning — including the 100 MB → 25 MB amendment and the residual
 * IS ~10 MB gap (O-020) — live in DL-027; change the value only via DL-027. It
 * must equal the BFF cap (`attachment-content-sniff.ts` MAX_ATTACHMENT_BYTES).
 *
 * Set to `undefined` to disable the size pre-check entirely — surfaces must then
 * render no size hint (FR-016a).
 *
 * LOCKSTEP: `myCases.detail.uploadLabel` and `compose.attachment.acceptedNote`
 * state this value as prose in three languages. Change it and change them
 * (FR-018). The too-large errors (`.uploadTooLarge`, `.blockTooLarge`) state no
 * size on purpose (D-232(a)) — do not add one.
 */
export const MEMBER_ATTACHMENT_MAX_BYTES: number | undefined = 25 * 1024 * 1024;

/**
 * Optional per-message attachment count guardrail (055 FR-016a).
 *
 * `undefined` — no count limit, and no count hint is rendered. This is the
 * customer's 2026-09-29 position: no count restriction until one is shown to be
 * needed. The prototype ships `5` here and renders an "Enintään 5 liitettä"
 * hint; that hint is precisely what `undefined` switches off.
 */
export const MEMBER_ATTACHMENT_MAX_COUNT: number | undefined = undefined;

/**
 * Optional reply-length ceiling (055 FR-016a), used to drive the composer's
 * character counter.
 *
 * Kept at the server's own `addMessage` cap so the counter never promises more
 * than the server accepts — `member.cases.addMessage` validates the trimmed text
 * against the IS contract's `AddMessageRequest.text` maxLength (1.7.0: 4 000) and
 * remains authoritative (DL-027, amended 2026-10-08). Counted in Unicode code
 * points, as the server and the IS count them (`replyLength`). `undefined` would
 * remove both the pre-check and the counter.
 *
 * LOCKSTEP: `myCases.detail.replyTooLong` states this value as prose in fi/sv/en;
 * change the copy together with the constant.
 */
export const MEMBER_REPLY_MAX_CHARS: number | undefined = 4_000;

/**
 * Reply length as `MEMBER_REPLY_MAX_CHARS` counts it: Unicode code points. Must
 * match the api's shared `codePointLength` (integration-service-client), which
 * `member.cases.addMessage` uses and the web package cannot import.
 */
export function replyLength(text: string): number {
  return [...text].length;
}

/**
 * Shared advisory pre-check predicate. ONE implementation of the fast
 * client-side size+type gate, used by BOTH web upload surfaces so the check
 * itself — not only the constants — cannot drift (P VIII). Advisory only: the
 * server sniff (type, 415) and, for size (413), the BFF upload route (25 MB)
 * then the IS (observed 10 MB) remain authoritative (O-020). Empty
 * MIME is allowed through (some browsers report none) — the server sniff decides.
 *
 * `maxBytes` is REQUIRED and accepts `undefined`, which skips the size arm
 * entirely (FR-016a). It deliberately has no default: a default parameter would
 * make "no ceiling" inexpressible, because JavaScript substitutes the default
 * for an explicitly-passed `undefined`. Callers pass
 * `MEMBER_ATTACHMENT_MAX_BYTES`, which is where the shared value lives.
 *
 * Returns the reason the file is blocked, or `null` if it passes.
 */
export function attachmentBlockReason(
  file: File,
  maxBytes: number | undefined,
): 'too-large' | 'unsupported' | null {
  if (maxBytes !== undefined && file.size > maxBytes) {
    return 'too-large';
  }
  if (file.type !== '' && !ALLOWED_ATTACHMENT_MIME.has(file.type)) {
    return 'unsupported';
  }
  return null;
}
